import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { getModbusData } from '../../redux/actions/otActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import OTDataTable from '../../components/OTDataTable';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';

import ModbusStyles from './Modbus.module.css';

const FRONTEND_MODBUS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildModbusSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({ errorCode, pageInfo, sourceMessage });

const ModbusScreen = () => {
	const dispatch = useDispatch();
	const [ dataLoading, setDataLoading ] = React.useState(false);

	const [ modbusData, setModbusData ] = React.useState({});
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setModbusSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildModbusSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearModbusSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				try {
					const data = await dispatch(getModbusData());
					if (
						data &&
						data.status &&
						Array.isArray(data.ModbusRequestCols) &&
						Array.isArray(data.ModbusRequestRows) &&
						Array.isArray(data.ModbusResponseCols) &&
						Array.isArray(data.ModbusResponseRows)
					) {
						setModbusData(data);
						clearModbusSupportError(true);
					} else {
						setModbusData({});
						setModbusSupportError(
							FRONTEND_MODBUS_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Modbus Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Modbus request did not return success')
						);
					}
				} catch (error) {
					setModbusData({});
					setModbusSupportError(
						FRONTEND_MODBUS_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Modbus Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ clearModbusSupportError, dispatch, setModbusSupportError ]
	);

	return (
		<Content currentMenu='OT' headerTitle='Modbus'>
			{dataLoading ? (
				<Loader />
			) : modbusData && Object.keys(modbusData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={ModbusStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={ModbusStyles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={ModbusStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={ModbusStyles.copySupportErrorButton}
									onClick={() => clearModbusSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={ModbusStyles.supportErrorBanner}>
							<div className={ModbusStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={ModbusStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={ModbusStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={ModbusStyles.copySupportErrorButton}
									onClick={() => clearModbusSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div className={ModbusStyles.tables}>
						<div className={ModbusStyles.table}>
							<OTDataTable
								title='Modbus Requests'
								searchPlaceholder='Search Here'
								cols={modbusData.ModbusRequestCols}
								rows={modbusData.ModbusRequestRows}
							/>
						</div>
						<div className={ModbusStyles.table}>
							<OTDataTable
								title='Modbus Responses'
								searchPlaceholder='Search Here'
								cols={modbusData.ModbusResponseCols}
								rows={modbusData.ModbusResponseRows}
							/>
						</div>
					</div>
				</Fragment>
			)}
		</Content>
	);
};

export default ModbusScreen;
